import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import { PageLayout } from '../../components/layout'
import { usePermissions } from '../../hooks/usePermissions'
import {
  getMenderConfigStatus,
  updateMenderConfig,
} from '../../services/operationsService'
import type { MenderConfigStatus, MenderConfigUpdate } from '../../types/device-upgrade'
import './MenderOtaSettings.css'

const { Text } = Typography

export const defaultMenderFormValues: MenderConfigUpdate = {
  enabled: true,
  base_url: '',
  username: '',
  password: '',
  tenant_token: '',
  clear_tenant_token: false,
  timeout_seconds: 10,
  device_sync_interval_minutes: 60,
  auto_accept_enabled: true,
}

const MENDER_URL_INVALID_MESSAGE = '请输入有效的 OTA 服务地址，必须以 https:// 或 http:// 开头'
const MENDER_CONFIGURED_HELP_MESSAGE = '修改配置后点击保存，系统会先测试连接，连接通过后才会保存。'
const MENDER_UNCONFIGURED_HELP_MESSAGE = '填写服务地址、用户名和密码后点击保存；系统会先测试连接，连接通过后才会保存。'
const MENDER_STATUS_LOAD_FAILED_MESSAGE = 'OTA 配置加载失败'
const MENDER_STATUS_LOAD_FAILED_DESCRIPTION = '请检查后台服务后重试'
const MENDER_PANEL_DESCRIPTION = '维护 OTA 服务地址、用户名和密码。'
const MENDER_TLS_HELP_MESSAGE = 'OTA 服务 TLS 证书校验失败，请检查后台配置地址是否与 OTA 公开地址一致，并确认后端已信任 OTA 证书或 CA。'
const MENDER_OTA_SAVE_FEEDBACK_KEY = 'mender-ota-save-feedback'
const MENDER_USERNAME_MAX_LENGTH = 128
const MENDER_PASSWORD_MAX_LENGTH = 128
const HIDDEN_MENDER_CONFIG_DEFAULTS = {
  enabled: true,
  tenant_token: null,
  clear_tenant_token: false,
  timeout_seconds: 10,
  device_sync_interval_minutes: 60,
  auto_accept_enabled: true,
} satisfies Pick<
  MenderConfigUpdate,
  | 'enabled'
  | 'tenant_token'
  | 'clear_tenant_token'
  | 'timeout_seconds'
  | 'device_sync_interval_minutes'
  | 'auto_accept_enabled'
>

let menderOtaSaveInFlight = false
const MENDER_OTA_SAVE_LOCK_KEY = '__yolopestMenderOtaSaveInFlight'

type WindowWithMenderOtaSaveLock = Window & {
  [MENDER_OTA_SAVE_LOCK_KEY]?: boolean
}

const isMenderOtaSaveInFlight = (): boolean => (
  menderOtaSaveInFlight
  || (
    typeof window !== 'undefined'
    && Boolean((window as WindowWithMenderOtaSaveLock)[MENDER_OTA_SAVE_LOCK_KEY])
  )
)

const setMenderOtaSaveInFlight = (value: boolean): void => {
  menderOtaSaveInFlight = value
  if (typeof window !== 'undefined') {
    ;(window as WindowWithMenderOtaSaveLock)[MENDER_OTA_SAVE_LOCK_KEY] = value
  }
}

const menderSettingsContainerStyle: React.CSSProperties = {
  width: '100%',
}

const menderSettingsCardBodyStyle: React.CSSProperties = {
  padding: 18,
}

const menderFormItemStyle: React.CSSProperties = {
  marginBottom: 8,
}

const normalizeOptionalText = (value?: string | null): string | null => {
  const normalized = String(value ?? '').trim()
  return normalized || null
}

const formatOtaDisplayMessage = (value: string): string =>
  value
    .replace(/(^|[^.])mender\s+server/gi, (_match, prefix: string) => `${prefix}OTA 服务`)
    .replace(/(^|[^.])mender/gi, (_match, prefix: string) => `${prefix}OTA`)

const formatOtaActionableError = (value: string): string => {
  const normalized = formatOtaDisplayMessage(value).trim()
  if (!normalized) {
    return normalized
  }
  if (normalized.includes('TLS 证书校验失败')) {
    return MENDER_TLS_HELP_MESSAGE
  }
  return normalized
}

const normalizeMenderConfigValues = (
  values: MenderConfigUpdate,
): MenderConfigUpdate => ({
  ...HIDDEN_MENDER_CONFIG_DEFAULTS,
  base_url: normalizeOptionalText(values.base_url),
  username: normalizeOptionalText(values.username),
  password: normalizeOptionalText(values.password),
})

const buildMenderFormValues = (
  status: MenderConfigStatus,
  currentValues: MenderConfigUpdate,
  forceOverwrite: boolean,
): MenderConfigUpdate => {
  const normalizedCurrent = normalizeMenderConfigValues(currentValues)
  const preserveCurrentInput = !forceOverwrite
    && !status.base_url
    && !status.username
    && Boolean(
      normalizedCurrent.base_url
      || normalizedCurrent.username
      || normalizedCurrent.password
      || normalizedCurrent.tenant_token,
    )

  return {
    ...HIDDEN_MENDER_CONFIG_DEFAULTS,
    base_url: preserveCurrentInput
      ? normalizedCurrent.base_url || ''
      : status.base_url || '',
    username: preserveCurrentInput
      ? normalizedCurrent.username || ''
      : status.username || '',
    password: preserveCurrentInput
      ? normalizedCurrent.password || ''
      : status.password || '',
  }
}

const getMenderStatusDescription = (
  status: MenderConfigStatus | null,
): string => (
  formatOtaDisplayMessage(
    status?.configured ? MENDER_CONFIGURED_HELP_MESSAGE : MENDER_UNCONFIGURED_HELP_MESSAGE,
  )
)

const getValidationDetailMessage = (detail: unknown): string | null => {
  if (typeof detail === 'string' && detail.trim()) {
    return detail
  }
  if (Array.isArray(detail)) {
    const firstMessage = detail
      .map((item) => {
        if (typeof item === 'string') {
          return item
        }
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg?: unknown }).msg ?? '')
        }
        return ''
      })
      .find((item) => item.trim())
    return firstMessage?.replace(/^Value error,\s*/, '') || null
  }
  if (detail && typeof detail === 'object') {
    const message = (detail as { message?: unknown; error?: unknown; detail?: unknown }).message
      || (detail as { message?: unknown; error?: unknown; detail?: unknown }).error
      || (detail as { message?: unknown; error?: unknown; detail?: unknown }).detail
    if (typeof message === 'string' && message.trim()) {
      return message
    }
  }
  return null
}

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) {
    return fallback
  }
  const data = error.response?.data
  const detail = getValidationDetailMessage(data?.detail)
  if (detail) {
    return detail
  }
  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message
  }
  if (typeof data?.error === 'string' && data.error.trim()) {
    return data.error
  }
  return fallback
}

interface MenderOtaConfigPanelProps {
  formValues?: MenderConfigUpdate
  hasUnsavedDraft?: boolean
  onDraftDirtyChange?: (dirty: boolean) => void
  onFormValuesChange?: (values: MenderConfigUpdate) => void
}

export const MenderOtaConfigPanel: React.FC<MenderOtaConfigPanelProps> = ({
  formValues,
  hasUnsavedDraft = false,
  onDraftDirtyChange,
  onFormValuesChange,
}) => {
  const { message } = App.useApp()
  const { canUpdateMenderOtaConfig } = usePermissions()
  const [menderForm] = Form.useForm<MenderConfigUpdate>()
  const [menderStatus, setMenderStatus] = useState<MenderConfigStatus | null>(null)
  const [localMenderFormValues, setLocalMenderFormValues] = useState<MenderConfigUpdate>(defaultMenderFormValues)
  const [menderStatusLoading, setMenderStatusLoading] = useState(true)
  const [menderSaving, setMenderSaving] = useState(false)
  const isControlled = formValues !== undefined
  const menderFormValues = formValues ?? localMenderFormValues
  const menderFormValuesRef = useRef(menderFormValues)
  const hasUnsavedDraftRef = useRef(hasUnsavedDraft)
  const savingRef = useRef(false)
  const pendingSaveClickRef = useRef(false)
  const pendingSaveClickReleaseTimerRef = useRef<number | null>(null)

  useEffect(() => {
    menderFormValuesRef.current = menderFormValues
    if (menderStatusLoading && !menderStatus) {
      return
    }
    menderForm.setFieldsValue({
      base_url: menderFormValues.base_url || '',
      username: menderFormValues.username || '',
      password: menderFormValues.password || '',
    })
  }, [menderForm, menderFormValues, menderStatus, menderStatusLoading])

  useEffect(() => {
    hasUnsavedDraftRef.current = hasUnsavedDraft
  }, [hasUnsavedDraft])

  const setMenderFormValues = useCallback((nextValues: React.SetStateAction<MenderConfigUpdate>) => {
    const resolvedValues = typeof nextValues === 'function'
      ? (nextValues as (currentValues: MenderConfigUpdate) => MenderConfigUpdate)(menderFormValuesRef.current)
      : nextValues

    menderFormValuesRef.current = resolvedValues
    if (isControlled) {
      onFormValuesChange?.(resolvedValues)
      return
    }
    setLocalMenderFormValues(resolvedValues)
  }, [isControlled, onFormValuesChange])

  const applyMenderStatusToForm = useCallback((status: MenderConfigStatus, forceOverwrite = false) => {
    if (forceOverwrite) {
      hasUnsavedDraftRef.current = false
      onDraftDirtyChange?.(false)
    }
    setMenderFormValues((currentValues) => (
      !forceOverwrite && hasUnsavedDraftRef.current
        ? currentValues
        : buildMenderFormValues(status, currentValues, forceOverwrite)
    ))
  }, [onDraftDirtyChange, setMenderFormValues])

  const loadMenderStatus = useCallback(async (forceOverwrite = false) => {
    setMenderStatusLoading(true)
    try {
      const status = await getMenderConfigStatus()
      setMenderStatus(status)
      applyMenderStatusToForm(status, forceOverwrite)
    } catch (error) {
      console.error(error)
      message.error('加载 OTA 配置失败')
    } finally {
      setMenderStatusLoading(false)
    }
  }, [applyMenderStatusToForm, message])

  useEffect(() => {
    void loadMenderStatus()
  }, [loadMenderStatus])

  const reserveMenderSave = useCallback(() => {
    if (isMenderOtaSaveInFlight() || savingRef.current || !canUpdateMenderOtaConfig) {
      return
    }
    setMenderOtaSaveInFlight(true)
    savingRef.current = true
    pendingSaveClickRef.current = true
    if (pendingSaveClickReleaseTimerRef.current !== null) {
      window.clearTimeout(pendingSaveClickReleaseTimerRef.current)
    }
    pendingSaveClickReleaseTimerRef.current = window.setTimeout(() => {
      pendingSaveClickReleaseTimerRef.current = null
      if (pendingSaveClickRef.current) {
        pendingSaveClickRef.current = false
        setMenderOtaSaveInFlight(false)
        savingRef.current = false
      }
    }, 1000)
  }, [canUpdateMenderOtaConfig])

  const acquireMenderSave = useCallback((): boolean => {
    if (!savingRef.current) {
      if (isMenderOtaSaveInFlight()) {
        return false
      }
      setMenderOtaSaveInFlight(true)
      savingRef.current = true
      return true
    }
    if (pendingSaveClickRef.current) {
      pendingSaveClickRef.current = false
      if (pendingSaveClickReleaseTimerRef.current !== null) {
        window.clearTimeout(pendingSaveClickReleaseTimerRef.current)
        pendingSaveClickReleaseTimerRef.current = null
      }
      return true
    }
    return false
  }, [])

  const releaseMenderSave = useCallback(() => {
    if (pendingSaveClickReleaseTimerRef.current !== null) {
      window.clearTimeout(pendingSaveClickReleaseTimerRef.current)
      pendingSaveClickReleaseTimerRef.current = null
    }
    pendingSaveClickRef.current = false
    setMenderOtaSaveInFlight(false)
    savingRef.current = false
  }, [])

  const handleMenderSave = useCallback(async () => {
    if (!acquireMenderSave()) {
      return
    }
    if (!canUpdateMenderOtaConfig) {
      releaseMenderSave()
      message.error('无权限修改 OTA 配置')
      return
    }
    let formValuesToSave: MenderConfigUpdate
    try {
      formValuesToSave = await menderForm.validateFields()
    } catch {
      releaseMenderSave()
      return
    }
    const values = normalizeMenderConfigValues({
      ...menderFormValues,
      ...formValuesToSave,
    })
    setMenderFormValues({
      ...values,
      password: values.password || '',
      base_url: values.base_url || '',
      username: values.username || '',
    })
    setMenderSaving(true)
    try {
      const saved = await updateMenderConfig(values)
      setMenderStatus(saved)
      applyMenderStatusToForm(saved, true)
      message.open({
        key: MENDER_OTA_SAVE_FEEDBACK_KEY,
        type: 'success',
        content: 'OTA 配置已保存',
      })
    } catch (error) {
      console.error(error)
      const errorMessage = formatOtaActionableError(extractApiErrorMessage(error, '保存 OTA 配置失败'))
      message.error(errorMessage)
    } finally {
      releaseMenderSave()
      setMenderSaving(false)
    }
  }, [acquireMenderSave, applyMenderStatusToForm, canUpdateMenderOtaConfig, menderForm, menderFormValues, message, releaseMenderSave, setMenderFormValues])

  const hasLoadedMenderStatus = !menderStatusLoading && Boolean(menderStatus)
  const menderStatusAlertType = !menderStatus
    ? 'error'
    : 'info'
  const menderStatusAlertMessage = !menderStatus
    ? MENDER_STATUS_LOAD_FAILED_MESSAGE
    : getMenderStatusDescription(menderStatus)
  const menderStatusAlertDescription = !menderStatus
    ? MENDER_STATUS_LOAD_FAILED_DESCRIPTION
    : undefined
  const renderMenderActions = () => (
    <Space size={12}>
      <Button
        icon={<ReloadOutlined />}
        onClick={() => void loadMenderStatus(true)}
        loading={menderStatusLoading}
        disabled={menderSaving}
      >
        刷新
      </Button>
      {canUpdateMenderOtaConfig ? (
        <Button
          type="primary"
          onMouseDownCapture={reserveMenderSave}
          onClick={() => void handleMenderSave()}
          onPointerDownCapture={reserveMenderSave}
          loading={menderSaving}
          disabled={menderSaving || !hasLoadedMenderStatus}
        >
          保存
        </Button>
      ) : null}
    </Space>
  )

  if (menderStatusLoading && !menderStatus) {
    return <div className="mender-ota-settings-panel" style={menderSettingsContainerStyle} />
  }

  return (
    <div className="mender-ota-settings-panel" style={menderSettingsContainerStyle}>
        <Card
          title="OTA 配置"
          extra={renderMenderActions()}
          styles={{ body: menderSettingsCardBodyStyle }}
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              type={menderStatusAlertType}
              showIcon
              message={menderStatusAlertMessage}
              description={menderStatusAlertDescription}
            />
            <Text type="secondary">{MENDER_PANEL_DESCRIPTION}</Text>
            <Form
              form={menderForm}
              layout="vertical"
              disabled={menderSaving || !hasLoadedMenderStatus}
              onValuesChange={(_, values) => {
                hasUnsavedDraftRef.current = true
                onDraftDirtyChange?.(true)
                setMenderFormValues((currentValues) => ({
                  ...currentValues,
                  ...values,
                }))
              }}
            >
              <Row gutter={[16, 8]}>
                <Col span={24}>
                  <Form.Item
                    name="base_url"
                    label="OTA 服务地址"
                    required
                    style={menderFormItemStyle}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: '请输入 OTA 服务地址',
                      },
                      {
                        validator: async (_: unknown, value: unknown) => {
                          const baseUrl = String(value || '').trim()
                          if (!baseUrl) {
                            return
                          }
                          if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
                            throw new Error(MENDER_URL_INVALID_MESSAGE)
                          }
                          try {
                            const parsedUrl = new URL(baseUrl)
                            if (!parsedUrl.hostname) {
                              throw new Error(MENDER_URL_INVALID_MESSAGE)
                            }
                          } catch {
                            throw new Error(MENDER_URL_INVALID_MESSAGE)
                          }
                        },
                      },
                    ]}
                  >
                    <Input placeholder="https://ota.example.com" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="username"
                    label="OTA 用户名"
                    required
                    style={menderFormItemStyle}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: '请输入 OTA 用户名',
                      },
                      {
                        max: MENDER_USERNAME_MAX_LENGTH,
                        message: `OTA 用户名不能超过 ${MENDER_USERNAME_MAX_LENGTH} 个字符`,
                      },
                    ]}
                  >
                    <Input placeholder="请输入 OTA 用户名" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="password"
                    label="OTA 密码"
                    required
                    style={menderFormItemStyle}
                    rules={[
                      {
                        validator: async (_: unknown, value: unknown) => {
                          const password = String(value || '').trim()
                          if (!password) {
                            throw new Error('请输入 OTA 密码')
                          }
                          if (password.length > MENDER_PASSWORD_MAX_LENGTH) {
                            throw new Error(`OTA 密码不能超过 ${MENDER_PASSWORD_MAX_LENGTH} 个字符`)
                          }
                        },
                      },
                    ]}
                  >
                    <Input.Password
                      placeholder="请输入 OTA 密码"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Space>
        </Card>
      </div>
  )
}

const MenderOtaSettings: React.FC = () => {
  const { t } = useTranslation(['navigation'])

  return (
    <PageLayout
      title={t('menu.menderOtaConfig', { defaultValue: 'OTA 配置' })}
      breadcrumb={[
        { title: t('menu.systemManagement', { defaultValue: '系统管理' }) },
        { title: t('menu.systemSettings', { defaultValue: '系统设置' }) },
        { title: t('menu.menderOtaConfig', { defaultValue: 'OTA 配置' }) },
      ]}
    >
      <MenderOtaConfigPanel />
    </PageLayout>
  )
}

export default MenderOtaSettings
